// `window.sentinel` — the answers you actually want when the console is noisy.
//
// This exists because of what the browser console looks like on a long-running
// cockpit tab: NS_BINDING_ABORTED lines from tiles MapLibre cancelled, a
// "Reporting Header: invalid JSON" warning that belongs to Google's tile
// servers, and nothing at all from us except the build stamp. None of that is
// wrong, and none of it answers the questions an operator has — is the update
// loop alive, how far has the cursor got, are the tiles churning, when did the
// last poll actually succeed.
//
// So rather than adding log lines to a stream nobody can read, the state is
// exposed on demand: `sentinel.stats()` in the console prints it once. Nothing
// here logs on a timer, because a cockpit tab stays open for days and a
// once-a-minute heartbeat is how a console becomes unreadable in the first
// place.
//
// It is deliberately READ-ONLY and deliberately not a debug hook: no method
// here mutates app state, so pasting it into a live console cannot change what
// an operator is looking at.

import { buildLabel } from './build';
import { tileStats } from './objects';

interface UpdateStats {
  /** The /api/updates cursor — the global rev this tab has consumed. */
  since: number;
  polls: number;
  ok: number;
  failed: number;
  /** Payloads that hit the server's cap, so the cursor advanced to the
   *  PAYLOAD's max rather than the global one. A climbing number here means
   *  the tab is behind and catching up one batch at a time. */
  truncated: number;
  lastPollAt: string | null;
  lastError: string | null;
  /** Entity changes seen, and how many of them actually needed a tile
   *  re-fetch. A large gap is this working as intended: live events reach the
   *  map through a client-side source and must not churn the tiles. */
  entityChanges: number;
  tileWorthy: number;
}

const updates: UpdateStats = {
  since: 0, polls: 0, ok: 0, failed: 0, truncated: 0,
  lastPollAt: null, lastError: null, entityChanges: 0, tileWorthy: 0,
};

/** One poll's outcome. Deliberately its own type rather than
 *  Partial<UpdateStats>: the stats are CUMULATIVE counters and these are a
 *  single poll's facts, so `failed` is a boolean here and a count there. */
interface PollNote {
  failed?: boolean;
  lastError?: string;
  since?: number;
  truncated?: boolean;
  entityChanges?: number;
  tileWorthy?: number;
}

export function notePoll(o: PollNote): void {
  updates.polls += 1;
  updates.lastPollAt = new Date().toISOString();
  if (o.failed) {
    updates.failed += 1;
    updates.lastError = o.lastError ?? 'poll failed';
    return;
  }
  updates.ok += 1;
  updates.lastError = null;
  if (o.since !== undefined) updates.since = o.since;
  if (o.truncated) updates.truncated += 1;
  updates.entityChanges += o.entityChanges ?? 0;
  updates.tileWorthy += o.tileWorthy ?? 0;
}

function stats(): Record<string, unknown> {
  return {
    build: buildLabel(),
    updates: { ...updates },
    tiles: tileStats(),
  };
}

export function installDiagnostics(): void {
  // Non-enumerable so it does not clutter autocomplete on `window`, and
  // configurable so a hot reload in dev can replace it.
  Object.defineProperty(window, 'sentinel', {
    value: Object.freeze({
      stats,
      get build() { return buildLabel(); },
      help: 'sentinel.stats() — update cursor, poll health and tile churn. '
        + 'Read-only; nothing here changes what the map is showing.',
    }),
    writable: false, enumerable: false, configurable: true,
  });
}
